import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/AuthContext";
import { Login_api } from "../../hooks/Apis";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { IoMdLogIn } from "react-icons/io";
import {
  IoLockClosed,
  IoMail,
  IoCall,
  IoPhonePortrait,
} from "react-icons/io5";

export default function LoginPage() {
  const navigate = useNavigate();
  const { user, login } = useAuth();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/auth/dashboard");
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(Login_api, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier, password }),
      });

      const data = await res.json();
      setLoading(false);

      if (data.success) login(data.user);
      else setError(data.message || "Login failed");
    } catch (err) {
      console.error(err);
      setError("Something went wrong");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-3 relative overflow-hidden bg-bg-pattern bg-cover bg-center bg-repeat">
      {/* Soft Blurs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -right-24 w-52 h-52 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-24 -left-24 w-52 h-52 bg-purple-400/20 rounded-full blur-3xl"></div>
      </div>

      {/* Card */}
      <div className="relative w-full max-w-sm bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl border border-white/30 overflow-hidden">
        <div className="p-5 text-center">
        
          <h1 className="text-lg font-semibold text-gray-900">Welcome Back</h1>
          <p className="text-xs text-gray-600 mt-1">
            Please login to your account
          </p>
        </div>

        {/* Form */}
        <div className="p-5 pt-2">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <IoMail className="absolute left-3 top-3.5 text-gray-400 text-lg" />
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Email or phone"
                className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white/50"
                required
                disabled={loading}
              />
            </div>

            <div className="relative">
              <IoLockClosed className="absolute left-3 top-3.5 text-gray-400 text-lg" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white/50"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-xs flex items-center">
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-5a1 1 0 112 0v1a1 1 0 11-2 0v-1zm0-6a1 1 0 012 0v4a1 1 0 11-2 0V7z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl font-semibold shadow-md transition-all duration-200 text-sm flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full mr-2"></div>
                  Signing in...
                </>
              ) : (
                <>
                  <IoMdLogIn className="mr-2 text-base" /> Sign In
                </>
              )}
            </button>
          </form>

          <div className="mt-5 pt-3 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-600 mb-2">
              Having trouble signing in?
            </p>
            <button className="inline-flex items-center justify-center bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg text-xs font-medium transition-transform hover:scale-105">
              <IoCall className="mr-2 text-sm" /> Contact Support{" "}
              <IoPhonePortrait className="ml-2 text-sm" />
            </button>
            <footer className="mt-3 text-center">
              <p className="text-[10px] text-gray-500">
                © {new Date().getFullYear()} Cyber Space Digital
              </p>
              <p className="text-[10px] text-gray-400">Secure login system</p>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}